import './review-modal.css';

interface ReviewModalProps {
  title?: string;
  description?: React.ReactNode;
  onGoBack: () => void;
  onConfirm: () => void;
  children: React.ReactNode;
}

export function ReviewModal({
  title = 'Review & confirm',
  description = (
    <>
      Please scan every field below before submitting. If anything looks wrong or missing, click{' '}
      <strong>Go Back &amp; Edit</strong> to return to the form.
    </>
  ),
  onGoBack,
  onConfirm,
  children,
}: ReviewModalProps) {
  return (
    <div className="review-modal">
      <div className="rv-overlay" onClick={onGoBack} />
      <div className="rv-dialog" role="dialog" aria-modal="true">
        <div className="rv-header">
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <div className="rv-content">{children}</div>
        <div className="rv-actions">
          <button type="button" className="rv-btn rv-btn-secondary" onClick={onGoBack}>
            Go Back &amp; Edit
          </button>
          <button type="button" className="rv-btn rv-btn-primary" onClick={onConfirm}>
            Confirm &amp; Submit
          </button>
        </div>
      </div>
    </div>
  );
}

export function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rv-sec">
      <h3 className="rv-sec-title">{title}</h3>
      <dl className="rv-dl">{children}</dl>
    </section>
  );
}

export function ReviewRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="rv-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** Formats a review value for display, returning null for empty/nullish values so `ReviewRow` hides itself. */
export function formatReviewValue(v: string | boolean | undefined | null): string | null {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : null;
  return String(v).trim() || null;
}

/** Parses a money-ish string/number (tolerating commas) and formats it as USD, or null if not a positive amount. */
export function formatReviewMoney(v: string | number | undefined | null): string | null {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) && n > 0
    ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : null;
}
